/**
 * 首屏弹窗管理服务
 * 1、所有目标弹窗都需要添加到PopupKey、PopupKeySort
 * 2、在弹窗组件的逻辑中，因为有的弹窗需要异步初始化，所以初始化完毕后统一使用popupToReady更改状态
 * 3、所有弹窗就绪后，会自动启动弹窗队列
 */

import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { ReplaySubject } from 'rxjs'

export enum PopupKey {
  /** 商业改版提示，旧商业模式用户在商业改版后首次登录时提示 */
  vipModify = 'vipModify',
  /** 欢迎弹窗提示部署，未部署终端时弹出 */
  welcome = 'welcome',
  /** 提示更新初始密码 */
  updatePassword = 'updatePassword',
  /** 所有服务已到期 */
  allServicesExpired = 'allServicesExpired',
}

type PopupReady = Record<PopupKey, boolean>

/** 弹窗队列排序 */
const PopupKeySort = [
  PopupKey.updatePassword,
  PopupKey.vipModify,
  PopupKey.welcome,
  PopupKey.allServicesExpired,
]

const EMPTY_INDEX = -999

@Injectable()
export class NotifyPopupService {
  /** 缓冲最近一次队列位置，登录等场景下弹窗组件尚未挂载订阅时也能收到启动通知 */
  onChangeActiveIndex = new ReplaySubject<number>(1)

  private activePopupIndex = EMPTY_INDEX

  /** 各弹窗状态-是否准备就绪 */
  popupReady = {} as PopupReady

  get activePopupKey () {
    return PopupKeySort[this.activePopupIndex]
  }

  constructor (private http: Http) {
    this.initAll()
  }

  initAll () {
    this.popupToUnreadyAll()
    this.activePopupIndex = EMPTY_INDEX
  }

  popupToUnreadyAll () {
    this.popupReady = Object.keys(PopupKey).reduce((acc, key) => {
      acc[PopupKey[key]] = false
      return acc
    }, {}) as PopupReady
  }

  closeAllPopup () {
    this.updateActiveIndex(EMPTY_INDEX)
  }

  updateActiveIndex (index: number) {
    this.activePopupIndex = index
    this.onChangeActiveIndex.next(index)
  }

  nextPopup () {
    this.updateActiveIndex(this.activePopupIndex + 1)
  }

  /** 启动弹窗队列 */
  startUp () {
    this.updateActiveIndex(0)
  }

  popupToUnready (popupKey: PopupKey) {
    this.popupReady[popupKey] = false
  }

  popupToReady (popupKey: PopupKey) {
    if (this.popupReady[popupKey] === true) {
      return
    }

    this.popupReady[popupKey] = true
    const isAllReady = Object.values(this.popupReady).reduce((acc, cur) => acc && cur)

    // 全部准备就绪，则启动弹窗队列，弹出弹窗
    if (isAllReady) {
      this.startUp()
    }
  }

  /** 用户级弹窗在这里修改为未准备态 */
  unReadyUserPopup () {
    this.popupToUnready(PopupKey.updatePassword)
    this.popupToUnready(PopupKey.allServicesExpired)
  }

  /** 团队级弹窗在这里修改为未准备态 */
  unReadyTeamPopup () {
    this.popupToUnready(PopupKey.vipModify)
    this.popupToUnready(PopupKey.welcome)
  }
}
